import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Table,
  Spin,
  Select,
  DatePicker,
  Input,
} from "antd";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import { STATUS_LABELS } from "./MyOrders";
import { BlobToDownloadFile } from "../../helper/commanFunction";
import "../../assets/css/po2-design.css";

const { RangePicker } = DatePicker;

// Task 1910-1914 (Feature 1865 / F7): vendor + retailer order report. Role-symmetric,
// same screen for both - the backend scopes rows by the caller's own MemberType (same
// pattern as MyOrders/GetMyOrdersByStatus), so this is FR-901/902 and FR-904 in one.
const usd = (v) => (v != null ? `$${Number(v).toFixed(2)}` : "-");

const OrderReport = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  // Task 1927 (Feature 1866 / F8): drill-through from the organizer dashboard - a figure
  // there navigates here with an initial filter via router state, e.g. { showId } or
  // { showId, counterpartyDealerId }. Plain page loads have no state, so this is a no-op.
  const location = useLocation();
  const [lineLevel, setLineLevel] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [dateRange, setDateRange] = useState(null);
  const [showId, setShowId] = useState(location.state?.showId ?? null);
  const [statusCode, setStatusCode] = useState(null);
  const [counterpartyDealerId, setCounterpartyDealerId] = useState(location.state?.counterpartyDealerId ?? null);
  const [referenceNumber, setReferenceNumber] = useState("");

  // Filter dropdown options come from a separate, always-unfiltered load - so narrowing
  // the table with one filter doesn't also shrink what's offered in the others.
  const [filterOptionsRows, setFilterOptionsRows] = useState([]);

  const buildFilters = () => ({
    lineLevel,
    dateFrom: dateRange?.[0] ? dateRange[0].startOf("day").toISOString() : null,
    dateTo: dateRange?.[1] ? dateRange[1].endOf("day").toISOString() : null,
    showId,
    statusCode,
    counterpartyDealerId,
    referenceNumber: referenceNumber || null,
  });

  // Multiple triggers can call this (mount, the lineLevel-toggle effect, the Apply
  // button) - if an earlier request resolves after a later one (e.g. the mount's
  // unfiltered fetch outlives a quick filtered Apply click), it must not clobber the
  // newer result. Same "latest request wins" guard as OrderContactPicker's double-submit
  // fix, applied here to out-of-order responses instead of duplicate submits.
  const requestIdRef = useRef(0);

  const fetchRows = () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    PurchaseOrderServices.GetOrderReport(buildFilters())
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
        if (res?.data?.status) {
          setRows(res.data.responseData || []);
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  };

  // Epic 1857 UI-redesign Phase 9: an organizer's caller-role always needs a showId filter
  // present (backend's ResolveOrganizerViewAsync 403s otherwise, since an organizer isn't
  // a party to any order the way a vendor/retailer's own unfiltered history naturally
  // scopes to them) - without this, the dropdown-options fetch silently failed for an
  // organizer arriving via drill-through, leaving showOptions empty and the Show filter
  // showing a bare numeric id instead of the show's name.
  useEffect(() => {
    PurchaseOrderServices.GetOrderReport({ lineLevel: false, showId: location.state?.showId ?? null }).then((res) => {
      if (res?.data?.status) {
        setFilterOptionsRows(res.data.responseData || []);
      }
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [lineLevel]);

  const showOptions = useMemo(() => {
    const seen = new Map();
    filterOptionsRows.forEach((r) => {
      if (r.showId != null && !seen.has(r.showId)) {
        seen.set(r.showId, r.showName || `Show #${r.showId}`);
      }
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [filterOptionsRows]);

  const counterpartyOptions = useMemo(() => {
    const seen = new Map();
    filterOptionsRows.forEach((r) => {
      if (r.counterpartyDealerId != null && !seen.has(r.counterpartyDealerId)) {
        seen.set(
          r.counterpartyDealerId,
          r.counterpartyCompanyName || r.counterpartyContactName || `#${r.counterpartyDealerId}`
        );
      }
    });
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [filterOptionsRows]);

  const handleExportCsv = () => {
    setCsvLoading(true);
    PurchaseOrderServices.ExportOrderReportCsv(buildFilters())
      .then((res) => {
        setCsvLoading(false);
        BlobToDownloadFile(res.data, `OrderReport-${Date.now()}.csv`);
      })
      .catch(() => setCsvLoading(false));
  };

  const handleExportPdf = () => {
    setPdfLoading(true);
    PurchaseOrderServices.ExportOrderReportPdf(buildFilters())
      .then((res) => {
        setPdfLoading(false);
        BlobToDownloadFile(res.data, `OrderReport-${Date.now()}.pdf`, "pdf");
      })
      .catch(() => setPdfLoading(false));
  };

  const orderColumns = [
    { title: t("Order #"), dataIndex: "poId", key: "poId", fixed: "left", width: 90 },
    { title: t("Reference #"), dataIndex: "referenceNumber", key: "referenceNumber" },
    {
      title: t("Status"),
      dataIndex: "statusCode",
      key: "statusCode",
      render: (status) => (
        <span className={`po2-st po2-st-${status}`}>{t(STATUS_LABELS[status] || status)}</span>
      ),
    },
    { title: t("Originator"), dataIndex: "originatorType", key: "originatorType" },
    {
      title: t("Created"),
      dataIndex: "createdDate",
      key: "createdDate",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    {
      title: t("Committed"),
      dataIndex: "committedDate",
      key: "committedDate",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    { title: t("Show"), dataIndex: "showName", key: "showName", render: (v) => v || "-" },
    {
      title: t("Counterparty"),
      key: "counterparty",
      render: (_, r) => r.counterpartyCompanyName || r.counterpartyContactName || "-",
    },
    { title: t("Recipient Type"), dataIndex: "recipientType", key: "recipientType" },
    { title: t("Terms"), dataIndex: "termsCode", key: "termsCode" },
    {
      title: t("Order Discount %"),
      dataIndex: "orderDiscountPct",
      key: "orderDiscountPct",
      render: (v) => (v != null ? `${v}%` : "-"),
    },
    { title: t("Early Pay %"), dataIndex: "earlyPayPercent", key: "earlyPayPercent" },
    { title: t("Early Pay Days"), dataIndex: "earlyPayDays", key: "earlyPayDays" },
    { title: t("Subtotal"), dataIndex: "subtotalAmount", key: "subtotalAmount", render: usd },
    { title: t("Discount Total"), dataIndex: "discountTotal", key: "discountTotal", render: usd },
    { title: t("Order Total"), dataIndex: "orderTotal", key: "orderTotal", render: usd },
    {
      title: t("Ad Hoc"),
      dataIndex: "hasAdHocLines",
      key: "hasAdHocLines",
      render: (v) => (v ? t("Yes") : t("No")),
    },
  ];

  const lineColumns = [
    { title: t("Order #"), dataIndex: "poId", key: "poId", fixed: "left", width: 90 },
    { title: t("Reference #"), dataIndex: "referenceNumber", key: "referenceNumber" },
    {
      title: t("Status"),
      dataIndex: "statusCode",
      key: "statusCode",
      render: (status) => (
        <span className={`po2-st po2-st-${status}`}>{t(STATUS_LABELS[status] || status)}</span>
      ),
    },
    {
      title: t("Created"),
      dataIndex: "createdDate",
      key: "createdDate",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    { title: t("Show"), dataIndex: "showName", key: "showName", render: (v) => v || "-" },
    {
      title: t("Counterparty"),
      key: "counterparty",
      render: (_, r) => r.counterpartyCompanyName || r.counterpartyContactName || "-",
    },
    { title: t("Line #"), dataIndex: "lineId", key: "lineId" },
    {
      title: t("Item"),
      key: "item",
      render: (_, r) => (r.isAdHoc ? r.adHocStyleNumber || t("Ad hoc") : r.jewelryInventoryId),
    },
    { title: t("Ad Hoc"), dataIndex: "isAdHoc", key: "isAdHoc", render: (v) => (v ? t("Yes") : t("No")) },
    { title: t("Qty"), dataIndex: "quantity", key: "quantity" },
    { title: t("List Price"), dataIndex: "listUnitPrice", key: "listUnitPrice", render: usd },
    { title: t("Discount Type"), dataIndex: "discountType", key: "discountType" },
    { title: t("Discount Value"), dataIndex: "discountValue", key: "discountValue" },
    { title: t("Net Unit Price"), dataIndex: "netUnitPrice", key: "netUnitPrice", render: usd },
    { title: t("Line Total"), dataIndex: "lineTotal", key: "lineTotal", render: usd },
  ];

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>{t("Order Report")}</h1>
        </div>
        <div className="po2-right">
          <span className="po2-seg">
            <button className={!lineLevel ? "po2-on" : ""} onClick={() => setLineLevel(false)}>{t("Order Level")}</button>
            <button className={lineLevel ? "po2-on" : ""} onClick={() => setLineLevel(true)}>{t("Line Level")}</button>
          </span>
          <button className="po2-btn po2-neutral po2-sm" onClick={handleExportCsv} disabled={csvLoading}>
            {t("Export CSV")}
          </button>
          <button className="po2-btn po2-neutral po2-sm" onClick={handleExportPdf} disabled={pdfLoading}>
            {t("Export PDF")}
          </button>
        </div>
      </div>

      <div className="po2-filters">
        <div className="po2-field">
          <label>{t("Date range")}</label>
          <RangePicker value={dateRange} onChange={setDateRange} />
        </div>
        <div className="po2-field">
          <label>{t("Show")}</label>
          <Select
            allowClear
            style={{ width: 180 }}
            value={showId || undefined}
            onChange={(v) => setShowId(v ?? null)}
            options={showOptions}
          />
        </div>
        <div className="po2-field">
          <label>{t("Status")}</label>
          <Select
            allowClear
            style={{ width: 160 }}
            value={statusCode || undefined}
            onChange={(v) => setStatusCode(v ?? null)}
            options={Object.keys(STATUS_LABELS).map((code) => ({ value: code, label: t(STATUS_LABELS[code]) }))}
          />
        </div>
        <div className="po2-field">
          <label>{t("Retailer / Vendor Account")}</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            value={counterpartyDealerId || undefined}
            onChange={(v) => setCounterpartyDealerId(v ?? null)}
            options={counterpartyOptions}
          />
        </div>
        <div className="po2-field">
          <label>{t("Reference #")}</label>
          <input
            className="po2-in"
            style={{ width: 160 }}
            placeholder={t("exact or partial")}
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchRows()}
          />
        </div>
        <button className="po2-btn" onClick={fetchRows}>
          {t("Apply Filters")}
        </button>
      </div>

      <div className="po2-panel po2-flush">
        <div className="po2-ph">
          <h3>{rows.length} {lineLevel ? t("lines") : t("orders")}</h3>
          <span className="po2-desc">{t("Scroll right for all columns - the order number stays pinned")}</span>
        </div>
        <Spin spinning={loading}>
          <Table
            rowKey={lineLevel ? "lineId" : "poId"}
            columns={lineLevel ? lineColumns : orderColumns}
            dataSource={rows}
            pagination={{ pageSize: 25 }}
            scroll={{ x: 1600, y: "calc(100vh - 380px)" }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default OrderReport;
